import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { bookingActions } from "../../../../store/reducers/booking";
import { uiActions } from "../../../../store/reducers/ui";

import { DateTime } from "luxon";

import { Grid, Stepper, Step, StepLabel, Typography, Button, Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import constants, { constant_names, FLOW, GA_STEP } from "../../../../utils/constants";

import LawArea from "./LawArea";
import MeetingType from "./MeetingType";
import Location from "./Location";
import Scheduler from "./Scheduler";
import ClientDetails from "./ClientDetails/ClientDetails";
import LoadingIcon from "../../../../images/LoadingIcon";

const STEPS = constants.STEPS;

export default function MeetingStepper(props) {
  const { loading, onSendToGA } = props;

  const branchesInfo = useSelector((state) => state.booking.branchesInfo);
  const requestType = useSelector((state) => state.booking.requestType);
  const clientDetails = useSelector((state) => state.booking.clientDetails);
  const globalLoading = useSelector((state) => state.loading.active);
  const activeStep = useSelector((state) => state.ui.activeStep);

  const [flowSteps, setFlowSteps] = useState(FLOW[requestType]);

  const activeStepName = flowSteps[activeStep];
  const meetingType = clientDetails.meetingType;
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  const maxStepperSize = isMobile ? 4 : 6;
  const headerHeight = isMobile ? "50px" : "60px";
  const footerHeight = "60px";

  const dispatch = useDispatch();

  const handlePreviousStep = () => {
    //Skips the address step if required independent of the steps and the order they are in
    if (activeStep === flowSteps.indexOf(STEPS.ADDRESS) + 1 && meetingType !== constants.MEETING_TYPES.IN_PERSON) {
      dispatch(bookingActions.updateClientDetails({ selectedBranch: null }));
      return dispatch(uiActions.updateActiveStep(flowSteps.indexOf(STEPS.ADDRESS) - 1));
    }

    if (requestType === constants.REQUEST_TYPES.INQUIRY && activeStep - 1 === flowSteps.indexOf(STEPS.LAW)) {
      setFlowSteps(FLOW[constants.REQUEST_TYPES.INQUIRY]);
    }

    dispatch(uiActions.previousStep());
  };

  const handleCancelDialogOpen = () => {
    dispatch(uiActions.updateCancelDialogOpen(true));
  };

  //Sets inquiry to phone automatically, sets branch to remote, and add area of law to client details
  const handleLawAreaSelection = (selectedLawArea, lawAreaDetails) => {
    dispatch(
      bookingActions.updateClientDetails({
        additionalInfo: { ...clientDetails.additionalInfo, lawArea: selectedLawArea, lawAreaDetails: lawAreaDetails },
      })
    );

    dispatch(uiActions.nextStep());
  };

  const handleMeetingTypeSelection = (selectedMeetingType) => {
    dispatch(bookingActions.updateClientDetails({ meetingType: selectedMeetingType }));

    if (selectedMeetingType === constants.MEETING_TYPES.IN_PERSON) {
      dispatch(uiActions.nextStep());
    } else {
      const nextStep =
        flowSteps.indexOf(STEPS.BOOKING) !== -1 ? flowSteps.indexOf(STEPS.BOOKING) : flowSteps.indexOf(STEPS.DETAILS);

      dispatch(bookingActions.updateClientDetails({ selectedBranch: "9999" })); //Remote
      dispatch(uiActions.updateActiveStep(nextStep));
    }
  };

  useEffect(() => {
    onSendToGA(GA_STEP[activeStepName], activeStepName);
  }, [activeStepName, onSendToGA]);

  return (
    <>
      <Box height={`${headerHeight}`} zIndex={2} width="100%" boxShadow={1} background="white">
        <Stepper
          activeStep={activeStep}
          sx={{
            height: "100%",
            justifyContent: activeStep + 1 > maxStepperSize && "end",
            padding: 1,
            overflowX: "hidden",
          }}
        >
          {flowSteps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};

            if (label === STEPS.LAW) {
              labelProps.optional = (
                <Typography variant="caption" display={{ xs: "none", sm: "block" }}>
                  {clientDetails.additionalInfo.lawArea || "Not Selected"}
                </Typography>
              );
            }

            if (label === STEPS.TYPE) {
              labelProps.optional = (
                <Typography variant="caption" display={{ xs: "none", sm: "block" }}>
                  {constant_names.MEETING_TYPE_NAMES[meetingType]}
                </Typography>
              );
            }

            if (label === STEPS.ADDRESS) {
              labelProps.optional = (
                <Typography variant="caption" display={{ xs: "none", sm: "block" }}>
                  {clientDetails.selectedBranch ? branchesInfo[clientDetails.selectedBranch]?.address : "Not Selected"}
                </Typography>
              );
            }

            if (label === STEPS.BOOKING) {
              labelProps.optional = (
                <Typography variant="caption" display={{ xs: "none", sm: "block" }}>
                  {!clientDetails.meetingDateTime
                    ? "Not Selected"
                    : DateTime.fromISO(clientDetails.meetingDateTime).toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
              );
            }

            return (
              <Step key={index} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      <Grid container height={`calc(100vh - ${headerHeight} - ${footerHeight})`}>
        {loading ? (
          <LoadingIcon />
        ) : (
          <>
            {activeStepName === STEPS.LAW && <LawArea onLawAreaSelected={handleLawAreaSelection} />}
            {activeStepName === STEPS.TYPE && <MeetingType onTypeSelected={handleMeetingTypeSelection} />}
            {activeStepName === STEPS.ADDRESS && <Location />}
            {activeStepName === STEPS.BOOKING && <Scheduler headerHeight={headerHeight} footerHeight={footerHeight} />}
            {activeStepName === STEPS.DETAILS && <ClientDetails />}
          </>
        )}
      </Grid>

      <Grid
        container
        justifyContent={{ xs: "space-between", sm: "space-around" }}
        alignItems="center"
        height={footerHeight}
        p={2}
      >
        {activeStep !== 0 && (
          <Grid item>
            <Button
              onClick={handlePreviousStep}
              disabled={globalLoading}
              sx={{ p: 0, m: 0, minHeight: 0, minWidth: 0 }}
            >
              Back
            </Button>
          </Grid>
        )}
        <Grid item>
          <Typography variant="body2">
            <a href={"tel:+" + constants.LITCO_NUMBER}>{constants.LITCO_NUMBER}</a>
          </Typography>
        </Grid>

        <Grid item>
          <Button
            onClick={handleCancelDialogOpen}
            disabled={globalLoading}
            sx={{ p: 0, m: 0, minHeight: 0, minWidth: 0 }}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </>
  );
}
